import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
// import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import useApi from 'hooks/useApi';
import useSelection from 'hooks/useSelection';
import MenuBookIcon from '@mui/icons-material/MenuBook';

interface InstructionComponentProps {
  transactionDetails: any;
  FullTransaction?: any;
  onSuccess?: () => void;
}

const InstructionComponent: React.FC<InstructionComponentProps> = ({
  transactionDetails,
  FullTransaction,
  onSuccess
}) => {
  const navigate = useNavigate();
  const { uploadEventData, showToast } = useApi();
  const { browserDetails, fingerprint, publicIp, location, captureLocationWithAddress } = useSelection();
  const [submitting, setSubmitting] = useState(false);

  // Extract instruction text from the transaction details
  const instructionText = FullTransaction?.step?.component?.settings?.instruction || 
                         transactionDetails?.step?.component?.settings?.instruction || 
                         transactionDetails?.component?.settings?.instruction || 
                         '';

  console.log('InstructionComponent - FullTransaction:', FullTransaction);
  console.log('InstructionComponent - transactionDetails:', transactionDetails);
  console.log('InstructionComponent - instructionText:', instructionText);

  const handleCancel = () => {
    navigate(-1); // Goes back to previous page
  };

  const handleAcknowledge = async () => {
    console.log("Acknowledging instruction...");
    try {
      setSubmitting(true);

      // Capture a fresh, geocoded location at submit time (only place the web
      // needs the address) instead of an eagerly-geocoded page-load location.
      const freshLocation = await captureLocationWithAddress();

      // Build the payload using the same structure as formData.tsx
      const payload: any = {
        comment: '',
        // Dashboard submission — tag as WEB so the backend driver gate/binding
        // (Rest/Stop/Swap) is bypassed: a manager is never the driver.
        eventSource: 'WEB',
        eventType: FullTransaction?.step?.stepType || FullTransaction?.eventType,
        step: FullTransaction?.step?.uid,
        browser: {
          ...browserDetails,
          fingerprint: fingerprint.fingerprint,
          publicIp
        },
        location: freshLocation ?? location,
        data: [], // No form fields for instruction - empty array
        ...(FullTransaction?.key ? { key: FullTransaction?.uid } : {}),
        ...(FullTransaction?.displayName ? { displayName: FullTransaction?.displayName } : {}),
        teamUid: FullTransaction?.step?.team?.uid || undefined
      };

      console.log("Instruction payload:", payload);

      let response = await uploadEventData(payload);
      let { success, data: respdata, message } = response;
      
      if (success) {
        showToast('Instructions acknowledged successfully', 'success');
        
        // Use the same success logic as formData.tsx
        const teamUid = FullTransaction?.team?.uid || FullTransaction?.teamUid;
        const uid = FullTransaction?.uid;
        const key = FullTransaction?.key;
        
        if (onSuccess) {
          onSuccess(); // This will refresh the transaction details
        } else if (teamUid && uid && key) {
          // Navigate back to transaction details if no onSuccess callback
          navigate(`/transactions/details/${teamUid}/${uid}/${key}`);
        } else {
          handleCancel();
        }
      } else {
        // Show the specific error message from the API response
        const errorMessage = respdata || message || 'Something went wrong!';
        console.log("Error message to show:", errorMessage);
        showToast(errorMessage, 'error');
      }
    } catch (error: any) {
      console.error('Instruction acknowledgment error:', error);
      // Extract the error message from the response data if available
      const errorMessage = error?.data || error?.message || 'An unexpected error occurred. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!instructionText) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No instruction content available.
        </Typography>
      </Box>
    );
  }

  // Split instruction text by newlines and render as formatted list
  const instructionLines = instructionText.split('\n').filter((line: string) => line.trim() !== '');

  return (
    <Card sx={{ borderRadius: 1, mb: 2 }}>
      <CardHeader
        title={
          <Typography
            fontWeight={600}
            variant="h6"
            sx={{
              color: 'white',
              fontSize: '0.9rem',
              letterSpacing: '0.025em'
            }}
          >
            {transactionDetails?.title || 'Instructions'}
          </Typography>
          
        }
        avatar={<MenuBookIcon sx={{ color: 'white' }} />}
        sx={{
          background: 'linear-gradient(90deg, #f15a22 0%, #f7941e 50%)',
          px: '5px',
          py: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          '& .MuiCardHeader-content': {
            flex: 1
          }
        }}
      />
      <CardContent sx={{ backgroundColor: '#fff', borderRadius: 2, p: 3 }}>
        <Box>
         
          <Box component="ol" sx={{ pl: 2, m: 0 }}>
            {instructionLines.map((line: string, index: number) => (
              <Typography 
                key={index} 
                component="li" 
                variant="body1" 
                sx={{ 
                  mb: 1, 
                  lineHeight: 1.6,
                  color: 'text.primary'
                }}
              >
                {line.replace(/^\d+\.\s*/, '')} {/* Remove numbering if already present */}
              </Typography>
            ))}
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ 
        justifyContent: 'flex-end', 
        background: '#fff', 
        borderBottomLeftRadius: 8, 
        borderBottomRightRadius: 8,
        borderTop: '1px solid',
        borderColor: 'grey.200',
        pt: 2,
        display: 'flex',
        gap: 2
      }}>
       
        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: '#132D76',
            '&:hover': {
              bgcolor: '#0f2458'
            },
            px: 5 
          }}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
          onClick={handleAcknowledge}
          disabled={submitting}
        >
          {submitting ? 'ACCEPTING...' : 'ACCEPT AND CONTINUE'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InstructionComponent;